import React, { useEffect, useState } from 'react';
import PageHeading from '../components/Common/PageHeading';
import { useTranslation } from 'react-i18next';
import useGetBeatInfo from '../hooks/useGetBeatInfo';
import ModalWrapper from '../components/Modal/ModalWrapper';
import PaymentItemsWrapper from '../components/Payment/PaymentItemsWrapper';
import StarbeatNotice from '../components/TermsNPrivacy/StarbeatNotice';
import StarbeatProductCard from '../components/Card/StarbeatProductCard';
import useApi from '../hooks/useApi';
import ERROR_CODES from '../constants/errorCodes';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { otpModalState } from '../recoil/otp/otpModalAtom';
import { loadingSpinnerState } from '../recoil/loadingSpinner/loadingSpinnerAtom';
import CountUp from 'react-countup';
import * as Sentry from '@sentry/react';
import { countryState } from '../recoil/country/countryAtom';
import useCheckTokenExpiry from '../hooks/useCheckTokenExpiry';

const StarbeatPurchase = () => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [starbeatProductList, setStarbeatProductList] = useState([]);
  const [selectedStarbeatProduct, setSelectedStarbeatProduct] = useState(null);

  const accessToken = localStorage.getItem('access_token');
  const countryData = useRecoilValue(countryState);
  const exp = localStorage.getItem('exp');

  const { t } = useTranslation();
  const { beatInfo, isLogin, error, hasAccessToken } = useGetBeatInfo(accessToken, countryData?.country);
  const isAccessTokenExpired = useCheckTokenExpiry(exp);

  const api = useApi();
  const navigate = useNavigate();
  const setOtpLogin = useSetRecoilState(otpModalState);
  const setIsLoading = useSetRecoilState(loadingSpinnerState);

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  // 단순 로그인 확인용
  const handleLoginCheck = () => {
    const errorCode = error?.response && error?.response.data && error?.response.data.code;
    if (!hasAccessToken) {
      clearSession();
      setOtpLogin(true);
      return false;
    }

    if (isAccessTokenExpired) {
      clearSession();
      setOtpLogin(true);
      return false;
    }

    if (!isLogin) {
      clearSession();
      setOtpLogin(true);
      return false;
    }

    if (
      errorCode === ERROR_CODES.invalidToken ||
      errorCode === ERROR_CODES.tokenExpired ||
      errorCode === ERROR_CODES.unauthorized
    ) {
      clearSession();
      setOtpLogin(true);
      return false;
    }

    return true;
  };

  const fetchStarbeatProductList = async () => {
    setIsLoading(true);

    try {
      const response = await api.get(`purchase/web_star_beat/product`);
      const newProducts = response?.data;
      setStarbeatProductList(newProducts);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        navigate('/error');
      }

      const errorCode = error.response && error.response.data && error.response.data.code;
      if (
        errorCode === ERROR_CODES.invalidToken ||
        errorCode === ERROR_CODES.tokenExpired ||
        errorCode === ERROR_CODES.unauthorized
      ) {
        navigate('/');
        localStorage.removeItem('access_token');
        localStorage.removeItem('userInfo');
        setOtpLogin(true); // 로그인 모달
        Sentry.setUser(null);
      } else {
        console.log(errorCode);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openPurchaseModal = (item) => {
    if (handleLoginCheck()) {
      setSelectedStarbeatProduct(item);
      setIsPaymentModalOpen(true);
    }
  };

  const closePurchaseModal = () => {
    setIsPaymentModalOpen(false);
  };

  useEffect(() => {
    fetchStarbeatProductList();
  }, [accessToken]);

  return (
    <>
      <div className={'ml-page'}>
        <PageHeading headingTxt={t('star_beat_purchase')} />
        <div className='layout-inner'>
          <div className='page-layout-wrapper'>
            <div className={'my-starbeat-wrapper'}>
              <div className='my-starbeat-left-wrapper'>
                <p className='my-starbeat-info-label'>{t('my_star_beats')}</p>
                <div className='my-starbeat-icon-info-group'>
                  <div className='my-starbeat-icon-group'>
                    <i className='icon icon-star-point' />
                  </div>
                  <span className='my-starbeat-info'>
                    {/* 비로그인 시 하이픈 처리, 로그인 시 마이스타비트 출력 */}
                    {!isLogin ? (
                      '-'
                    ) : beatInfo?.data?.star_beat ? (
                      <CountUp end={beatInfo?.data?.star_beat} duration={1} />
                    ) : (
                      '0'
                    )}
                  </span>
                </div>
              </div>
              <div className='my-starbeat-right-wrapper'>
                <ul className='my-starbeat-top-notice-group'>
                  <li className='my-starbeat-top-notice-list'>
                    <p className='my-starbeat-top-notice-txt'>{t('star_beat_top_notice_01')}</p>
                  </li>
                  <li className='my-starbeat-top-notice-list'>
                    <p className='my-starbeat-top-notice-txt'>{t('star_beat_top_notice_02')}</p>
                  </li>
                </ul>
              </div>
            </div>

            <div className='starbeat-product-wrapper'>
              {starbeatProductList.map((item) => (
                <div key={item.pk} className='starbeat-product-box'>
                  <StarbeatProductCard
                    item={item}
                    onClick={() => {
                      openPurchaseModal(item);
                    }}
                  />
                </div>
              ))}
            </div>

            <div className='starbeat-notice-page-desc-wrapper'>
              <StarbeatNotice />
            </div>
          </div>
        </div>
      </div>
      <ModalWrapper
        isOpen={isPaymentModalOpen}
        isBottomButton={true}
        onClose={closePurchaseModal}
        mobileAlignTop={true}
      >
        {isPaymentModalOpen ? (
          <PaymentItemsWrapper
            productDetailData={selectedStarbeatProduct}
            selectedItems={[]}
            paymentProductType={'starbeat'}
          />
        ) : null}
      </ModalWrapper>
    </>
  );
};

export default StarbeatPurchase;
